// Change REACT_APP_DATA_BASE_URL to point to S3/CDN in production.
const BASE = (process.env.REACT_APP_DATA_BASE_URL || '').replace(/\/$/, '');
const DATA_ROOT = 'lang-data-source';

const RETRY_COUNT = 2;
const RETRY_DELAY_MS = 1000;
const REQUEST_TIMEOUT_MS = 10 * 1000;
const MEMORY_TTL_MS = 5 * 60 * 1000;
const memoryCache = new Map();
const inFlight = new Map();

export function dataUrl(path) {
  const cleanPath = path.replace(/^\//, '');
  return `${BASE}/${DATA_ROOT}/${cleanPath}`;
}

async function fetchWithRetry(url, attempt = 0) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null;
  try {
    const r = await fetch(url, controller ? { signal: controller.signal } : undefined);
    if (!r.ok) {
      const error = new Error(`HTTP ${r.status} for ${url}`);
      error.retryable = r.status === 408 || r.status === 429 || r.status >= 500;
      throw error;
    }
    return r;
  } catch (err) {
    if (err.retryable !== false && attempt < RETRY_COUNT) {
      await new Promise((res) => setTimeout(res, RETRY_DELAY_MS * (attempt + 1)));
      return fetchWithRetry(url, attempt + 1);
    }
    throw err;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export function fetchData(path, { force = false } = {}) {
  const now = Date.now();
  const cached = memoryCache.get(path);
  if (!force && cached && now - cached.writtenAt < MEMORY_TTL_MS) {
    return Promise.resolve(cached.value);
  }
  if (!force && inFlight.has(path)) return inFlight.get(path);

  const request = fetchWithRetry(dataUrl(path))
    .then((response) => response.json())
    .then((value) => {
      memoryCache.set(path, { value, writtenAt: Date.now() });
      return value;
    })
    .finally(() => {
      if (inFlight.get(path) === request) inFlight.delete(path);
    });
  inFlight.set(path, request);
  return request;
}

function normalizedLocale(locale) {
  const code = String(locale || 'en').trim().toLowerCase();
  return /^[a-z]{2,3}$/.test(code) ? code : 'en';
}

export async function fetchDataWithEnglishFallback(pathForLocale, locale, options) {
  const code = normalizedLocale(locale);
  try {
    return await fetchData(pathForLocale(code), options);
  } catch (error) {
    if (code === 'en') throw error;
    return fetchData(pathForLocale('en'), options);
  }
}

export async function fetchTextWithEnglishFallback(pathForLocale, locale) {
  const code = normalizedLocale(locale);
  try {
    return await fetchWithRetry(dataUrl(pathForLocale(code))).then((response) => response.text());
  } catch (error) {
    if (code === 'en') throw error;
    return fetchWithRetry(dataUrl(pathForLocale('en'))).then((response) => response.text());
  }
}

export function clearDataMemoryCache(path) {
  if (path) {
    memoryCache.delete(path);
    inFlight.delete(path);
  } else {
    memoryCache.clear();
    inFlight.clear();
  }
}
